import { useEffect, useState } from "react";
import { RiCheckboxCircleFill, RiFileDownloadLine, RiInformationLine } from "react-icons/ri";
import { Link, useSearchParams } from "react-router-dom";

import ErrorIconSvg from "@/assets/svg/error-icon.svg?react";
import LabelledCombobox from "@/components/combobox/LabelledCombobox";
import MissionCombobox from "@/components/combobox/MissionCombobox";
import InfoAlert from "@/components/InfoAlert";
import Loader from "@/components/Loader";
import SearchInput from "@/components/SearchInput";
import Select from "@/components/Select";
import Table from "@/components/Table";
import Tooltip from "@/components/Tooltip";
import { DOMAINS_LABELS, STATUS_PLR } from "@/constants";
import api from "@/services/api";
import { captureError } from "@/services/error";
import { compactMissionFilters, searchMissions } from "@/services/mission";
import useStore from "@/services/store";
import exportCSV from "@/utils/csv";

const TABLE_HEADER = [
  { title: "Mission", key: "title.keyword", width: "35%" },
  { title: "Places disponibles", key: "places" },
  { title: "Ville", key: "city.keyword", width: "20%" },
  { title: "Créée le", key: "createdAt" },
  { title: "Acceptées par l'API ?", key: "statusCode.keyword" },
];

const Flux = ({ moderated }) => {
  const { publisher } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    size: 25,
    page: Number(searchParams.get("page")) || 1,
    sortBy: "createdAt",
    status: searchParams.get("status") || null,
    comment: searchParams.get("comment") || null,
    domain: searchParams.get("domain") || null,
    activity: searchParams.get("activity") || null,
    cities: searchParams.has("cities") ? searchParams.getAll("cities") : [],
    organizationIds: searchParams.has("organizationIds") ? searchParams.getAll("organizationIds") : [],
    search: searchParams.get("search") || "",
  });
  const [options, setOptions] = useState({
    status: [],
    comments: [],
    domains: [],
    activities: [],
    organizations: [],
  });
  const [lastImport, setLastImport] = useState();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hideAlert, setHideAlert] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.post("/import/search", { publisherId: publisher.id, size: 1 });
        if (!res.ok) {
          throw res;
        }
        setLastImport(res.data.length ? res.data[0] : null);
      } catch (error) {
        captureError(error, { extra: { publisherId: publisher.id } });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await searchMissions({ ...filters, publisherId: publisher.id }, { signal: controller.signal });

        if (!res.ok) {
          throw res;
        }
        setData(res.data);
        setOptions(res.aggs);
        setTotal(res.total);

        const newSearchParams = new URLSearchParams();
        Object.entries(compactMissionFilters(filters)).forEach(([key, value]) => newSearchParams.append(key, value));
        setSearchParams(newSearchParams);
      } catch (error) {
        captureError(error, { extra: { filters } });
      }
      setLoading(false);
    };

    fetchData();
    return () => controller.abort();
  }, [filters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await searchMissions({ ...filters, publisherId: publisher.id, size: 10000, page: 1 });

      if (!res.ok) {
        throw res;
      }
      const csv = [];
      res.data.forEach((mission) => {
        const val = {};
        val["ClientId"] = mission.clientId;
        val["Titre"] = mission.title;
        val["Place"] = mission.places;
        val["Description"] = `"${mission.description}"`;
        val["Organisation"] = mission.organizationName;
        val["Ville"] = `${mission.city} - ${mission.country}`;
        val["Domaine"] = mission.domain;
        val["Activité"] = (mission.activities || []).join(", ");
        val["Statut"] = mission.statusCode;
        csv.push(val);
      });
      exportCSV(`missions ${publisher.name}`, csv);
    } catch (error) {
      captureError(error, { extra: { filters } });
    }
    setExporting(false);
  };

  return (
    <div className="space-y-12 p-4 sm:p-12">
      <title>API Engagement - Missions partagées - Vos Missions</title>
      {moderated && !hideAlert && (
        <InfoAlert onClose={() => setHideAlert(true)}>
          <p className="text-base">Pour toutes les missions acceptées par l’API, JeVeuxAider.gouv.fr pratique une modération avant de les diffuser.</p>
          <Link to="moderated-mission" className="text-blue-france underline">
            Suivre la modération
          </Link>
        </InfoAlert>
      )}
      <div className="flex flex-col gap-6">
        <SearchInput
          label="Rechercher une mission par mot-clé"
          className="w-full sm:w-96"
          value={filters.search}
          onChange={(search) => setFilters({ ...filters, search })}
          placeholder="Rechercher par mot-clé"
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Select
            options={options.status.map((e) => ({ value: e.key, label: STATUS_PLR[e.key], count: e.doc_count }))}
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.value })}
            placeholder="Acceptées par l'API ?"
            loading={loading}
          />
          <Select
            options={options.comments.filter((e) => Boolean(e.key)).map((e) => ({ value: e.key, label: e.key, count: e.doc_count }))}
            value={filters.comment}
            onChange={(e) => setFilters({ ...filters, comment: e.value })}
            placeholder="Motif d'erreur"
            loading={loading}
          />
          <Select
            options={options.domains.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseigné" : DOMAINS_LABELS[e.key], count: e.doc_count }))}
            value={filters.domain}
            onChange={(e) => setFilters({ ...filters, domain: e.value })}
            placeholder="Domaines"
            loading={loading}
          />

          <Select
            options={options.activities.map((e) => ({ value: e.key === "" ? "none" : e.key, label: e.key === "" ? "Non renseignée" : e.key, count: e.doc_count }))}
            value={filters.activity}
            onChange={(e) => setFilters({ ...filters, activity: e.value })}
            placeholder="Activités"
            loading={loading}
          />
          <MissionCombobox
            id="city"
            values={filters.cities}
            onChange={(cities) => setFilters({ ...filters, cities })}
            placeholder="Villes"
            filters={`publishers=${publisher.id}&field=city`}
          />
          <LabelledCombobox
            id="organizationIds"
            values={filters.organizationIds}
            onChange={(organizationIds) => setFilters({ ...filters, organizationIds })}
            placeholder="Organisations"
            options={options.organizations}
          />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <h2 className="text-2xl font-bold">
              <span role="status" aria-live="polite" aria-atomic="true">{`${total.toLocaleString("fr")} missions partagées`}</span>
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-text-mention text-base">Dernière synchronisation le {lastImport ? new Date(lastImport.startedAt).toLocaleDateString("fr") : "N/A"}</p>
              {lastImport && new Date(lastImport.startedAt) > new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 1) ? (
                <RiCheckboxCircleFill role="img" aria-label="Synchronisation à jour" className="text-success shrink-0 text-base" />
              ) : (
                <ErrorIconSvg role="img" aria-label="Synchronisation en retard" className="fill-error h-4 w-4 shrink-0" />
              )}
              <Link to="/settings" className="link whitespace-nowrap">
                Paraméter mon flux de missions
              </Link>
            </div>
          </div>

          <button className="tertiary-btn shrink-0" onClick={handleExport}>
            {exporting ? <Loader /> : <RiFileDownloadLine className="mr-2" aria-hidden="true" />}
            Exporter
          </button>
        </div>

        <Table
          caption="Liste des missions"
          header={TABLE_HEADER}
          pagination
          page={filters.page}
          pageSize={filters.size}
          onPageChange={(page) => setFilters({ ...filters, page })}
          total={total}
          loading={loading}
          sortBy={filters.sortBy}
          onSort={(sortBy) => setFilters({ ...filters, sortBy })}
          auto
        >
          {data.map((item, i) => (
            <tr key={i} className={`${i % 2 === 0 ? "bg-table-even" : "bg-table-odd"} table-row`}>
              <td className="p-4">
                <Link to={`/mission/${item._id}`} className="text-blue-france line-clamp-3">
                  {item.title}
                </Link>
                {item.organizationName && <p className="text-sm">{item.organizationName}</p>}
              </td>
              <td className="px-4">{item.places}</td>
              <td className="px-4">{item.city}</td>
              <td className="px-4">{new Date(item.createdAt).toLocaleDateString("fr")}</td>
              <td className="px-6">
                <div className="flex items-center gap-1">
                  {item.statusCode === "ACCEPTED" ? (
                    <RiCheckboxCircleFill role="img" aria-label="Acceptée par l'API" className="text-success text-2xl" />
                  ) : (
                    <ErrorIconSvg role="img" aria-label="Non acceptée par l'API" className="fill-error h-6 w-6" />
                  )}
                  {item.statusComment ? (
                    <Tooltip
                      ariaLabel="Voir le commentaire de statut"
                      triggerClassName="text-text-mention"
                      tooltipClassName="border-grey-border w-64 border bg-white p-4 text-sm shadow-lg"
                      content={item.statusComment}
                    >
                      <RiInformationLine className="text-2xl" aria-hidden="true" />
                    </Tooltip>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </div>
  );
};

export default Flux;
